import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import CustomLoadingOverlay from 'components/billing/common/CustomLoadingOverlay'
import { TabPanel } from 'components/common'
import AddressDisplay from 'components/common/address/AddressDisplay'
import HHAccordion from 'components/common/HHAccordion'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import { UNASSIGNED_ROUTE } from 'containers/new-route-manager/settings'
import T from 'T'
import { captureError } from 'utils/error'

import ConfiguredServicesV2 from '../content/configured-services/ConfiguredServicesV2'
import HoldsAccordion from '../content/holds/HoldsAccordion'
import ActiveFilters from '../content/routing/Graphs/ActiveFilters'
import EventsCalendar from '../content/routing/Graphs/EventsCalendar'
import RouteFilter from '../content/routing/Graphs/RouteFilter'
import Routing from '../content/routing/Routing'
import { useCalendarData } from '../content/routing/useCalendarData'
import Filters from '../content/work-orders/filters'
import { WORK_ORDER_STATUS_FILTER_MENU_OPTIONS } from '../content/work-orders/filters/work-order-status-filter/settings'
import WorkOrderLayout from '../content/work-orders/layout/WorkOrderLayout'
import { FORM_MODEL } from '../content/work-orders/settings'
import { ConfiguredServices } from '../tables'

const ROUTING_TAB = 'routing'
const WORK_ORDERS_TAB = 'workOrders'

const ServicesContentPanel = ({
  selectedLocationId,
  currentLocation,
  currentLocationAddress,
  currentLocationAddressName,
  seeService,
  isCSRedesignEnabled,
  accountId,
  activeLocations,
}) => {
  const workOrderForm = useForm({ defaultValues: { ...FORM_MODEL } })
  const [selectedTab, setSelectedTab] = useState(ROUTING_TAB)

  const selectedRoutes = workOrderForm.watch('selectedRoutes')
  const selectedServices = workOrderForm.watch('selectedServices')
  const selectedWorkOrderStatuses = workOrderForm.watch('selectedWorkOrderStatuses')
  const selectedDateRange = workOrderForm.watch('selectedDateRange')
  const routesOptions = workOrderForm.watch('routesOptions')
  const servicesOptions = workOrderForm.watch('servicesOptions')
  const routeCounts = workOrderForm.watch('routeCounts')

  const {
    expandWindowIfNeeded,
    ensureDateInWindow,
    isLoading: isCalendarDataLoading,
    isFetched,
    isError: isCalendarDataError,
    error: calendarDataError,
    routingHeatmap,
    routesByDate,
    apiHeatmap,
    byDate,
    vacationHoldData,
    paymentsByDate,
  } = useCalendarData({
    accountId,
    // Everything below renders under seeService, so without this a failure has nowhere to surface.
    skip: !seeService,
    locationId: selectedLocationId,
    selectedRoutes,
    selectedServices,
    selectedWorkOrderStatuses,
    routesOptions,
    servicesOptions,
  })

  // Capture only: the tab panels name the failure on screen, but nothing else records it for on-call.
  const reportedError = useRef(null)
  useEffect(() => {
    if (!isCalendarDataError) {
      reportedError.current = null
      return
    }
    if (reportedError.current === calendarDataError) return
    reportedError.current = calendarDataError
    captureError(calendarDataError, { surface: 'services-calendar-facets' })
  }, [isCalendarDataError, calendarDataError])

  const selectedRoute = useMemo(() => {
    if (!selectedRoutes || selectedRoutes.length === 0) return 'All'
    if (selectedRoutes.length === 1) return selectedRoutes[0].id
    return 'All'
  }, [selectedRoutes])

  const uniqueRoutes = useMemo(() => {
    if (!routesOptions) return []
    return routesOptions
      .filter(r => r.id !== UNASSIGNED_ROUTE.id)
      .map(r => ({ id: r.id, name: r.value }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [routesOptions])

  const handleRouteFilter = useCallback(
    routeId => {
      const options = routesOptions || []
      if (routeId === 'All') {
        workOrderForm.setValue('selectedRoutes', options)
      } else if (routeId === UNASSIGNED_ROUTE.id) {
        workOrderForm.setValue('selectedRoutes', [{ id: UNASSIGNED_ROUTE.id, value: UNASSIGNED_ROUTE.name }])
      } else {
        const route = options.find(r => r.id === routeId)
        if (route) {
          workOrderForm.setValue('selectedRoutes', [{ id: route.id, value: route.value }])
        }
      }
    },
    [workOrderForm, routesOptions]
  )

  const handleRemoveRoute = useCallback(
    routeToRemove => {
      const updated = selectedRoutes.filter(r => r.id !== routeToRemove.id)
      workOrderForm.setValue('selectedRoutes', updated)
    },
    [selectedRoutes, workOrderForm]
  )

  const handleRemoveService = useCallback(
    serviceToRemove => {
      const updated = selectedServices.filter(s => s.id !== serviceToRemove.id)
      workOrderForm.setValue('selectedServices', updated)
    },
    [selectedServices, workOrderForm]
  )

  const handleRemoveStatus = useCallback(
    statusToRemove => {
      const updated = selectedWorkOrderStatuses.filter(s => s.id !== statusToRemove.id)
      workOrderForm.setValue('selectedWorkOrderStatuses', updated)
    },
    [selectedWorkOrderStatuses, workOrderForm]
  )

  const handleClearDateRange = useCallback(() => {
    workOrderForm.setValue('selectedDateRange', [null, null])
  }, [workOrderForm])

  const handleResetAllFilters = useCallback(() => {
    workOrderForm.setValue('selectedRoutes', routesOptions || [])
    workOrderForm.setValue('selectedServices', servicesOptions || [])
    workOrderForm.setValue('selectedWorkOrderStatuses', WORK_ORDER_STATUS_FILTER_MENU_OPTIONS)
    workOrderForm.setValue('selectedDateRange', [null, null])
  }, [workOrderForm, routesOptions, servicesOptions])

  const handleTabChange = useCallback((event, newValue) => {
    setSelectedTab(newValue)
  }, [])

  return (
    <>
      {selectedLocationId && currentLocation ? (
        <AddressDisplay address={currentLocationAddress} addressName={currentLocationAddressName} sx={{ mb: 3 }} variant="large" />
      ) : (
        <Box sx={{ mb: 3 }}>
          <Typography noWrap color="text.primary" fontWeight={600} variant="h4">
            {T.ALL_LOCATIONS_SELECTED}
          </Typography>
          <Typography noWrap color="text.secondary" variant="body2">
            {`${activeLocations.length} active locations`}
          </Typography>
        </Box>
      )}

      {seeService && (
        <>
          {isCSRedesignEnabled ? (
            <ConfiguredServicesV2
              accountId={accountId}
              ensureDateInWindow={ensureDateInWindow}
              expandWindowIfNeeded={expandWindowIfNeeded}
              isCalendarDataLoading={isCalendarDataLoading}
              locationId={selectedLocationId}
            />
          ) : (
            <ConfiguredServices accountId={accountId} locationId={selectedLocationId} />
          )}

          <Box mt={2}>
            <HoldsAccordion accountId={accountId} />
          </Box>

          <FormProvider {...workOrderForm}>
            <Box mb={8} mt={2}>
              <HHAccordion
                isExpandCollapseHeightSame
                isExpanded
                showCustomActions
                withNewColors
                customActionContent={<Filters />}
                showActionIcon={false}
                summary={T.ROUTING_AND_WORK_ORDERS}
              >
                <Box sx={{ position: 'relative', minHeight: !isFetched ? 300 : 'auto' }}>
                  {!isFetched && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 10 }}>
                      <CustomLoadingOverlay id="services-accordion" />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 2, pb: 0 }}>
                    <Tabs
                      sx={{
                        flexShrink: 0,
                        minHeight: 36,
                        '& .MuiTab-root': {
                          textTransform: 'capitalize',
                          minHeight: 36,
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 400,
                          width: '200px',
                          color: 'text.secondary',
                          '&.Mui-selected': {
                            color: 'common.white',
                            bgcolor: 'primary.main',
                          },
                        },
                      }}
                      TabIndicatorProps={{ sx: { display: 'none' } }}
                      value={selectedTab}
                      onChange={handleTabChange}
                    >
                      <Tab label={T.ROUTING} value={ROUTING_TAB} />
                      <Tab label={T.WORK_ORDERS} value={WORK_ORDERS_TAB} />
                    </Tabs>
                    {routesOptions && (
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <RouteFilter
                          routeCounts={routeCounts || {}}
                          selectedRoute={selectedRoute}
                          uniqueRoutes={uniqueRoutes}
                          onRouteChange={handleRouteFilter}
                        />
                      </Box>
                    )}
                  </Box>

                  <TabPanel index={ROUTING_TAB} value={selectedTab}>
                    {isCalendarDataError ? (
                      <HHSectionPlaceholder title={T.FAILED_TO_LOAD_WORK_ORDERS} />
                    ) : (
                      <Routing
                        accountId={accountId}
                        ensureDateInWindow={ensureDateInWindow}
                        expandWindowIfNeeded={expandWindowIfNeeded}
                        isActive={selectedTab === ROUTING_TAB}
                        isCalendarLoading={isCalendarDataLoading}
                        locationId={selectedLocationId}
                        routesByDate={routesByDate}
                        routingHeatmap={routingHeatmap}
                      />
                    )}
                  </TabPanel>

                  <TabPanel index={WORK_ORDERS_TAB} value={selectedTab}>
                    {isCalendarDataError ? (
                      <HHSectionPlaceholder title={T.FAILED_TO_LOAD_WORK_ORDERS} />
                    ) : (
                      <EventsCalendar
                        apiHeatmap={apiHeatmap}
                        byDate={byDate}
                        ensureDateInWindow={ensureDateInWindow}
                        expandWindowIfNeeded={expandWindowIfNeeded}
                        isActive={selectedTab === WORK_ORDERS_TAB}
                        isCalendarLoading={isCalendarDataLoading}
                        locationId={selectedLocationId}
                        paymentsByDate={paymentsByDate}
                        vacationHoldData={vacationHoldData}
                      />
                    )}
                  </TabPanel>

                  <ActiveFilters
                    selectedRange={selectedDateRange}
                    selectedRoutes={selectedRoutes}
                    selectedServices={selectedServices}
                    selectedWorkOrderStatuses={selectedWorkOrderStatuses}
                    onClearDateRange={handleClearDateRange}
                    onRemoveRoute={handleRemoveRoute}
                    onRemoveService={handleRemoveService}
                    onRemoveStatus={handleRemoveStatus}
                    onResetAll={handleResetAllFilters}
                  />
                  <WorkOrderLayout hasFilters accountId={accountId} locationId={selectedLocationId} />
                </Box>
              </HHAccordion>
            </Box>
          </FormProvider>
        </>
      )}
    </>
  )
}

ServicesContentPanel.propTypes = {
  selectedLocationId: PropTypes.string,
  currentLocation: PropTypes.object,
  currentLocationAddress: PropTypes.object,
  currentLocationAddressName: PropTypes.string,
  seeService: PropTypes.bool.isRequired,
  isCSRedesignEnabled: PropTypes.bool.isRequired,
  accountId: PropTypes.string.isRequired,
  activeLocations: PropTypes.array.isRequired,
}

export default ServicesContentPanel
